import { ImportResult } from '../converters/types/converters.types';
import { SpotWeather } from '../utils/route-weather-worker';
import { ShipModel } from './ship-model';

export enum LegType {
    RhumbLine = "Rhumbline",
    GreatCircle = "GreatCircle"
}

export enum GeometryType {
    Point = "Point",
    LineString = "LineString"
}

export interface WeatherLimitValue
{
    avoid: number|null;
    warn: number|null;
}

export interface WeatherLimitPeriodValue
{
    avoid: { max: number|null, min: number|null };
    warn: { max: number|null, min: number|null };
}

export interface WeatherLimits
{
    Waves: WeatherLimitValue;
    Wind10M: WeatherLimitValue;
    Wind50M: WeatherLimitValue;
    Visibility: WeatherLimitValue;
    Icing: WeatherLimitValue;
    Hurricanes: WeatherLimitValue;
    TropicalStorms: WeatherLimitValue;
    Depressions: WeatherLimitValue;
    WindGusts10M: WeatherLimitValue;
    WindGusts50M: WeatherLimitValue;
    RiskWindSpeed: WeatherLimitValue;
    RiskWaveHeight: WeatherLimitValue;
    HeadWind: WeatherLimitValue;
    BeamWind: WeatherLimitValue;
    FollowingWind: WeatherLimitValue;
    BeamWavesHeight: WeatherLimitValue;
    BeamWavesPeriod: WeatherLimitPeriodValue;
    HeadWavesHeight: WeatherLimitValue;
    HeadWavesPeriod: WeatherLimitPeriodValue;
    FollowingWavesHeight: WeatherLimitValue;
    FollowingWavesPeriod: WeatherLimitPeriodValue;
    IceConcentration: WeatherLimitValue;
}

export interface VoyageRoutePointFeature
{
    type: "Feature";
    geometry: {
        coordinates: [number,number];
        type: "Point";
    };
    properties: {
        [index:string]: any;
        eta?: string; // User input - for points where we need to arrive at a specific time (usually for pilot points)
        pilotPoint?: boolean;
        speed_maximum?: number; // Maximum speed restriction (in m/s)
        speed_minimum?: number; // Minimum speed restriction (in m/s)
        type?: "Hard"|"Navigational"|"Weather"|"Informational"; // "Weather" is only possible when using DTNs routing
        isPassed?: boolean; // Whether this waypoint has already been passed
        vesselPositionAt?: string; // This waypoint was injected as the vessels current position at the given timestamp

        // Below properties should be present for a fully calculated route
        timestamp?: string; // calculated time at this point

        // Below properties may be added for the passage plan
        turnrate?: string;
        name?: string; // User defined name/details for this waypoint
        catzoc?: string; // Category Zone of Confidence
        lop?: string // Line of Position (LOP) interval
        max_x_track?: string; // Max cross track error
        min_depth?: number; // Minimum depth in meters
        tide?: number; // Tide value in meters
        squat?: number; // Squat in meters
        ukc?: number; // Under Keel Clearance in meters
        // openWaters is used for squat calculation - it determines whether the waterway is confined (ie. shallow and/or narrow) or open
        // Default value is false (ie. confined) for berth->pilot and pilot->berth, true (ie. open) for pilot->pilot
        openWaters?: boolean;

        // Below properties are related to the parametric roll calculation
        // Will only be present on "real" points when no informational waypoints are present on the leg, otherwise
        // they are present in the informational waypoints
        parametric_roll_ratio_wave?: number // Ratio between natural roll period and wave encounter period
        parametric_roll_ratio_swell?: number // Ratio between natural roll period and swell encounter period
        is_high_risk_roll_wave?: boolean // Is risk of parametric roll high, based on parametric_roll_ratio_wave
        is_high_risk_roll_swell?: boolean // Is risk of parametric roll high, based on parametric_roll_ratio_swell
        is_high_risk_roll?: boolean // True if either is_high_risk_roll_wave or is_high_risk_roll_swell
    };
}

export interface WeatherSummary
{
    max: { value: number; direction?: number; };
    min: { value: number; direction?: number; };
    average: { value: number; direction?: number; };
}

export interface SubscriptionInfo
{
    forecast: string;
    importedAt: string;
    id: string;
}

export interface WarningSegment
{
    actual: number;
    coordinates: [number,number][];
}

export interface WeatherWarning
{
    type: keyof WeatherLimits;
    limit: number;
    actual?: number; // Old format
    segments?: WarningSegment[]; // New format
}

export interface ParametricRollWarning
{
    coordinates: [number,number][]; // start and end coordinates
    timeStart: string; // Time at start coordinates
    timeEnd: string; // Time at end coordinates
}

export interface VoyageRouteLineFeature
{
    type: "Feature";
    geometry: {
        coordinates: [number,number][];
        type: "LineString";
    };
    properties: {
        [index:string]: any;

        // Below properties should be present for a fully calculated route
        course?: number; // Not present for GreatCircle legs
        leg_type?: "Rhumbline"|"GreatCircle";
        distance?: number; // in km
        speed_over_ground?: number; // in m/s
        speed_through_water?: number; // in m/s
        propulsion_power?: number; // in kW
        propulsion_power_lower?: number; // in kW
        propulsion_power_upper?: number; // in kW
        main_engine_rpm?: number;
        main_engine_rpm_lower?: number;
        main_engine_rpm_upper?: number;
        weather?: { [index:string]: WeatherSummary };
        warnings?: {
            weather: {
                warn: WeatherWarning[];
                avoid: WeatherWarning[];
            };
            parametricRoll?: ParametricRollWarning[];
        };
        informationalWaypoints?: VoyageRoutePointFeature[];
        spotWeathers?: SpotWeather[]
    };
}

export interface RoutePassagePlanProperties {
    description: string,
    reminders: string
    notEcdisCompliant: boolean,
    encCellsUsed: string,
    booksAndTables: string,
    chartsAndLightsNotes: string,
    sailingDirections: string,
    listOfRadioSignals: string,
    listOfLights: string,
    gpsEcdisRadarMap: string,
    paperCharts: string,
    pubCorrectedToWeek: string,
    tideTables: string,
    publicationsNotes: string,
    mastersOrders: string,
    safetyMessages: {
        navareaRegions: string,
        navtexStations: string
    },
    weatherInfo: {
        navareaRegions: string,
        navtexStations: string,
        info: string
    },
    equipmentStatus: {
        operationalCondition: string,
        failures: string
    },
    weatherRouting: {
		required: boolean,            
		acknowledged: boolean,
		pilotPointAcknowledged: boolean 
	} 
}

export type RouteGeometry = (VoyageRouteLineFeature|VoyageRoutePointFeature)[]

export interface VoyageRoute
{
    name?: string;
    uiType?: string;
    creationTime?: string;
    isPassedUpdate?: string; // time for last update of isPassed property on route_geometry
    bestPracticeRouteId?: string; //add best practice route id 
    route_info?: {
        eta: string; // calculated value, valid for shortestWeatherOptimized and weatherOptimized route types
        etd: string;
        speed_over_ground: number; // in m/s, describes speed used to calculate the route
        co2: number; // in mT, describes calculated CO2 emission
        totals: {
            non_eca: {
                time: number; // in hours, format: total hours; describes total route time sailed through regular areas
                fuel_cost: number; // monetary value of total regular fuel cost to cover full route without any currency reference
                vessel_cost: number; // monetary value without any currency reference
                distance: number; // in km (available for ShortestDistance), describes total route distance to be sailed through regular areas with regular fuel
                fuel: number; // in mT, describes total amount of fuel to be utilized during calculated route (fuel consumption of main engine)
                fuel_lower?: number; // in mT, lower bound of fuel consumption
                fuel_upper?: number; // in mT, upper bound of fuel consumption
            },
            eca: { // not filled in until ECA support; same as above but with light fuel and via ECA areas
                time: number;
                fuel_cost: number;
                vessel_cost: number;
                distance: number;
                fuel: number;
                fuel_lower?: number; // in mT, lower bound of fuel consumption
                fuel_upper?: number; // in mT, upper bound of fuel consumption
            }
        },
        rpm: number;
        weather: {
            forecast_versions: string[];
            subscriptions?: SubscriptionInfo[];
            summary: { [index:string ]: WeatherSummary };
            hasLowResData?: boolean;
            hasHighResData?: boolean;
            weatherPoints?: VoyageRoutePointFeature[];
        };
        calculationInput: {
            forecast_versions: string[];
            draftFore: number;
            draftAft: number;
            gmt?: number;
            etd: string;
            eta: string;
            weatherLimits: WeatherLimits;
        };
        hydraRequestId?: string;
        hydraErrors?: any[];
        whaleZonesApplied?: boolean;
    };
    route_geometry: RouteGeometry,
    passagePlan?: RoutePassagePlanProperties,
    routeCreatedBy?: string
}

export interface UpdatePlanOptions
{
    routeIndex: number; // -1 to add as a new route
    autoApprove?: boolean;
    selectRoute?: boolean;
}

export interface CalculationFailedDetails
{
    error: string;
    scheduleId?: string;
    hydraRequestId?: string;
    hydraErrorBody?: any;
    routeIndex?: number;
}

export interface NextWaypointPayload {
    routeGeometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature>;
    position: number[];
    maxAllowedDeviation: number;
}

export interface RouteTypesPayload {
    imo: number;
    schedule_id: string;
}

export interface RouteTypePost{
    imo: number;
    plan_id?: number;
    route_type?: string;
    schedule_id: string;
}


export interface NextWaypointResult {
    input: NextWaypointPayload;
    result: string;
    nextWPIndex: number;
    deviation: number;
}

export interface RoutingJobOptions
{
    draft?: number; // Either draft or draftAft and draftFore needs to be present
    draftAft?: number;
    draftFore?: number;
    routeType?: "ShortestDistance"|"ShortestWeatherOptimized"|"WeatherOptimized";
    shipModel?: ShipModel;
    uiRouteIndex?: number;
    etd?: number;
    eta?: number;
    weatherLimits?: WeatherLimits;
    forecastVersion?: string;
    encValidation?: boolean;
    numberOfReefers?: number;
    reeferConsumption?: number;
    dangerousCargo?: boolean;
    updatePlan?: UpdatePlanOptions;
    planScheduleId?: string;
    addRouteToModel?: boolean;
    maxSegmentDistance?: number;
    gmt?: number;

    // Override ship dimensions, margins and speed (for testing purposes)
    airDraft?: number;
    lpp?: number;
    beam?: number;
    blockCoefficient?: number;
    aftMargin?: number;
    airMargin?: number;
    draftMargin?: number;
    forwardMargin?: number;
    portsideMargin?: number;
    starboardMargin?: number;
    minSpeed?: number;
    maxSpeed?: number;
    imo?: number;
    whaleZonesApplied?: boolean;
    passagePlan?: RoutePassagePlanProperties;
}

export type RouteCalcStatus = "idle"|"calculating"|"failed";

export interface UIRouteWithMetadata
{
    status: RouteCalcStatus;
    route: VoyageRoute|null;
    error: string|null;
    selected: boolean;
    crossingWhaleZones: boolean;
}

export interface FailedUIRouteWithMetadata extends UIRouteWithMetadata {
    index: number;
}

export interface PositionAndHeadingInTime
{
    position: [number, number];
    projectedPosition: [number, number]
    heading: number;
}

export enum Backends
{
    DTN = "DTN",
    EncApi = "encApi",
    RouteOptimiser = "RouteOptimiser",
    Hydra = "Hydra"
}

export interface MissingPoints {
    beforeFistPilotPoint: (VoyageRoutePointFeature | VoyageRouteLineFeature)[] | undefined;
    afterLastPilotPoint: (VoyageRoutePointFeature | VoyageRouteLineFeature)[] | undefined;
}

export interface VoyageSummaryPayload {
    arrivalPortCallId: number | null | undefined;
    departurePortCallId: number | null | undefined;
}

export abstract class VoyageRoutingModelBase extends EventTarget
{
    abstract shipModel: ShipModel;
    abstract get jobCount(): number;
    abstract get routes(): VoyageRoute[];
    abstract get uiRoutes(): UIRouteWithMetadata[];
    abstract get activePlan(): string|null;
    abstract get routingBackend(): Backends;

    abstract changeRoutingBackend(newBackend: Backends): void;
    abstract setActivePlan(scheduleId: string|null): Promise<void>;
    abstract addRoute(route: VoyageRoute): number;
    abstract getRoute(index: number): VoyageRoute;
    abstract deleteRoute(index: number): void;
    abstract postRoutingJob(startPos: [number, number], endPos: [number, number], options: RoutingJobOptions): Promise<VoyageRoute>;
    abstract postRoutingJobUsingRoute(route: VoyageRoute, options: RoutingJobOptions): Promise<VoyageRoute>;
    abstract postEncAugmentRouteJob(route: VoyageRoute): Promise<VoyageRoute|null>;
    abstract getNextWaypointIndex(maxAllowedDeviation: number, position: number[], routeGeometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature>): Promise<NextWaypointResult>;

    abstract setWeatherLimits(weatherLimits: WeatherLimits): void;
    abstract getWeatherLimits(): WeatherLimits;

    abstract planHasActiveCalculations(scheduleId: string): boolean;

    abstract importFilesRaw(files: FileList): Promise<ImportResult[]>;
    abstract importFile(file: File, options: RoutingJobOptions, missingPoints: MissingPoints | null, mergeWith?: VoyageRoute, addCurrentPosition?: boolean): Promise<VoyageRoute>;
    abstract importShipModelFile(file: File): Promise<void>;

    abstract getPositionAndHeadingInTime(timestamp: number, route: VoyageRoute): PositionAndHeadingInTime|null;
    abstract saveRoute(voyageRoute: VoyageRoute, uiRouteIndex: number, sceduleId: string): Promise<void>;
    abstract routeTypeFilter(payload: RouteTypesPayload[]): Promise<RouteTypesPayload[]>;
}

let VoyageRoutingModel: VoyageRoutingModelBase|null = null;

export function setVoyageRoutingModel(model: VoyageRoutingModelBase)
{
    VoyageRoutingModel = model;
}

export enum VoyageRoutingEvents {
   CenterWarningCoordinates = "set-center-on-warning-coordinates",
}

export { VoyageRoutingModel };
